/**
 * AdminLiveOpsTab — Phase A.
 *
 * Glanceable, low-cognitive-load realtime view. Adaptive polling pauses
 * when the document is hidden. Subtabs: Overview, Users, Events, Funnels, Alerts.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Activity, Users, Zap, AlertTriangle, Sparkles, Crown, Film,
  Flame, Heart, MapPin, Smartphone, Wifi, WifiOff, History,
} from "lucide-react";
import { toast } from "sonner";
import SessionHistoryPanel from "./SessionHistoryPanel";

// Adaptive polling intervals (ms)
const POLL = {
  users: { focused: 5000, blurred: 30000 },
  feed: { focused: 4000, blurred: 30000 },
  funnels: { focused: 6000, blurred: 60000 },
  alerts: { focused: 3000, blurred: 0 }, // pause completely when blurred
};

const STATUS_COLOUR = {
  online: "bg-emerald-500",
  idle: "bg-amber-500",
  offline: "bg-zinc-500",
};

const EVENT_ICON = {
  premium_purchase: "💎",
  founding_explorer_claim: "👑",
  yir_export_ready: "🎥",
  signup: "🎉",
  ai_suggestion: "✨",
  follow: "💌",
  streak_milestone_30: "🔥",
  trip_completed: "🌍",
  referral_accepted: "🤝",
  achievement_unlock: "🏆",
  scrapbook_upload: "📸",
  item_added: "➕",
  comment: "💬",
  reaction: "❤️",
};

// Use the page visibility API
function useDocumentVisible() {
  const [visible, setVisible] = useState(typeof document !== "undefined" ? !document.hidden : true);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = (typeof iso === "string") ? new Date(iso) : iso;
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function formatSessionDuration(s) {
  if (!s && s !== 0) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

// ── alert toast bridge with dedup of seen alert IDs ──
const _seenAlertIds = new Set();
function maybeToastAlert(alert) {
  if (!alert?.id || _seenAlertIds.has(alert.id)) return;
  _seenAlertIds.add(alert.id);
  // Cap memory
  if (_seenAlertIds.size > 500) {
    const arr = Array.from(_seenAlertIds);
    arr.slice(0, 200).forEach((id) => _seenAlertIds.delete(id));
  }
  toast(`${alert.icon || "✨"} ${alert.title}`, {
    description: alert.message,
    duration: 6000,
  });
}

const AdminLiveOpsTab = () => {
  const visible = useDocumentVisible();
  const [users, setUsers] = useState({ users: [], totals: { online: 0, idle: 0, offline: 0, total: 0 } });
  const [feed, setFeed] = useState({ events: [], count: 0 });
  const [funnels, setFunnels] = useState({ funnels: [], total_online: 0 });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const seenSinceRef = useRef(60); // seconds window we ask the alerts endpoint for

  const pickInterval = (key) => visible ? POLL[key].focused : POLL[key].blurred;

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/live-users`);
      setUsers(r.data || { users: [], totals: { online: 0, idle: 0, offline: 0, total: 0 } });
    } catch (_) {}
  }, []);

  const fetchFeed = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/live-feed?limit=80`);
      setFeed(r.data || { events: [], count: 0 });
    } catch (_) {}
  }, []);

  const fetchFunnels = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/live-funnels`);
      setFunnels(r.data || { funnels: [], total_online: 0 });
    } catch (_) {}
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/live-alerts?since_seconds=${seenSinceRef.current}`);
      const alerts = r.data?.alerts || [];
      alerts.forEach(maybeToastAlert);
      // Bump window slightly so the next call only sees newer alerts
      seenSinceRef.current = 30;
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchUsers(); fetchFeed(); fetchFunnels(); fetchAlerts();
    const ints = [];
    if (pickInterval("users") > 0) ints.push(setInterval(fetchUsers, pickInterval("users")));
    if (pickInterval("feed") > 0) ints.push(setInterval(fetchFeed, pickInterval("feed")));
    if (pickInterval("funnels") > 0) ints.push(setInterval(fetchFunnels, pickInterval("funnels")));
    if (pickInterval("alerts") > 0) ints.push(setInterval(fetchAlerts, pickInterval("alerts")));
    return () => ints.forEach(clearInterval);
  }, [visible, fetchUsers, fetchFeed, fetchFunnels, fetchAlerts]);

  // Session inspector
  useEffect(() => {
    if (!selectedUserId) return setSessionDetail(null);
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/user-session/${selectedUserId}`);
        if (!cancelled) setSessionDetail(r.data || null);
      } catch (_) {
        if (!cancelled) setSessionDetail(null);
      }
    })();
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/user-session/${selectedUserId}`);
        if (!cancelled) setSessionDetail(r.data || null);
      } catch (_) {}
    }, visible ? 5000 : 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedUserId, visible]);

  return (
    <div className="space-y-4" data-testid="admin-live-ops">
      {/* Environment banner — surfaces live db_fingerprint + backend host so
          we can spot prod/preview drift instantly. */}
      <EnvBanner />

      {/* Top KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<Wifi className="w-4 h-4 text-emerald-500" />}
          label="Online now"
          value={users.totals.online}
          accent="emerald"
        />
        <KpiCard
          icon={<Activity className="w-4 h-4 text-amber-500" />}
          label="Idle"
          value={users.totals.idle}
          accent="amber"
        />
        <KpiCard
          icon={<Zap className="w-4 h-4 text-primary" />}
          label="Live events (6h)"
          value={feed.count}
          accent="primary"
        />
        <KpiCard
          icon={<Sparkles className="w-4 h-4 text-pink-500" />}
          label="Active funnels"
          value={funnels.funnels.filter(f => f.active > 0).length}
          accent="pink"
        />
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4">
          <TabsList className="bg-card border border-border inline-flex min-w-max">
            <TabsTrigger value="overview"><Activity className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
            <TabsTrigger value="users"><Users className="w-3.5 h-3.5 mr-1" />Users</TabsTrigger>
            <TabsTrigger value="events"><Zap className="w-3.5 h-3.5 mr-1" />Events</TabsTrigger>
            <TabsTrigger value="funnels"><Sparkles className="w-3.5 h-3.5 mr-1" />Funnels</TabsTrigger>
            <TabsTrigger value="alerts"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Alerts</TabsTrigger>
            <TabsTrigger value="sessions"><MapPin className="w-3.5 h-3.5 mr-1" />Sessions</TabsTrigger>
            <TabsTrigger value="history" data-testid="livehistory-tab-trigger"><History className="w-3.5 h-3.5 mr-1" />History</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">🟢 Live users (top 6)</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {users.users.slice(0, 6).map((u) => (
                  <UserRow key={u.session_id} u={u} compact onClick={() => setSelectedUserId(u.user_id)} />
                ))}
                {users.users.length === 0 && <p className="text-sm text-muted-foreground">Nobody online right now.</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">⚡ Latest events</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                {feed.events.slice(0, 8).map((e) => (
                  <EventRow key={e.id} e={e} />
                ))}
                {feed.events.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader><CardTitle className="text-base">All sessions ({users.totals.total})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {users.users.map((u) => (
                <UserRow key={u.session_id} u={u} onClick={() => setSelectedUserId(u.user_id)} />
              ))}
              {users.users.length === 0 && <p className="text-sm text-muted-foreground">No active sessions.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Live event stream
                <Badge variant="outline" className="text-[10px]">newest first</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {feed.events.map((e) => <EventRow key={e.id} e={e} />)}
              {feed.events.length === 0 && <p className="text-sm text-muted-foreground">No events yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="funnels">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {funnels.funnels.map((f) => (
              <Card key={f.key}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{f.label}</p>
                    <p className="text-3xl font-bold mt-1">{f.active}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${f.active > 0 ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"}`} />
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts">
          <Card>
            <CardHeader><CardTitle className="text-base">Recent founder alerts (last 60s)</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-2">
                💎 / 👑 alerts also push to your phone. Other alerts toast in this admin tab.
              </p>
              <div className="space-y-2">
                <AlertsLog />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions">
          <SessionInspector
            users={users.users}
            selectedUserId={selectedUserId}
            onSelect={setSelectedUserId}
            sessionDetail={sessionDetail}
          />
        </TabsContent>

        <TabsContent value="history">
          <SessionHistoryPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ── small components ──

function KpiCard({ icon, label, value, accent }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center bg-${accent}-500/10`}>
          {icon}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function UserRow({ u, onClick, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted/40 text-left transition-colors"
      data-testid={`live-user-${u.user_id}`}
    >
      <span className={`w-2 h-2 rounded-full ${STATUS_COLOUR[u.status] || "bg-zinc-500"} ${u.status === "online" ? "animate-pulse" : ""}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {u.user_name}
          {u.premium && <Crown className="w-3 h-3 inline-block ml-1 text-amber-500" />}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          {u.current_feature || u.current_route || "—"}
          {u.active_modal && <span className="ml-1 text-primary">· {u.active_modal}</span>}
        </p>
      </div>
      {!compact && (
        <div className="text-right text-[10px] text-muted-foreground">
          <div>{formatSessionDuration(u.session_seconds)}</div>
          <div className="flex items-center gap-1 justify-end mt-0.5">
            <Smartphone className="w-3 h-3" />
            {u.device || u.platform || "?"}
          </div>
        </div>
      )}
    </button>
  );
}

function EventRow({ e }) {
  const icon = EVENT_ICON[e.type] || "·";
  const importanceColor = {
    critical: "text-red-500",
    high: "text-pink-500",
    normal: "text-foreground",
    low: "text-muted-foreground",
  }[e.importance] || "text-foreground";
  return (
    <div className="flex items-center gap-2 text-sm py-1" data-testid={`live-event-${e.id}`}>
      <span className="text-base flex-shrink-0">{icon}</span>
      <span className={`flex-1 min-w-0 truncate ${importanceColor}`}>
        <span className="font-medium">{e.user_name || "Someone"}</span>
        <span className="text-muted-foreground"> · {e.label || e.type}</span>
      </span>
      <span className="text-[10px] text-muted-foreground flex-shrink-0">{timeAgo(e.created_at)}</span>
    </div>
  );
}

function AlertsLog() {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fetcher = async () => {
      try {
        const r = await axios.get(`${API}/admin/live-alerts?since_seconds=900`);
        if (!cancelled) setAlerts(r.data?.alerts || []);
      } catch (_) {}
    };
    fetcher();
    const iv = setInterval(fetcher, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  if (alerts.length === 0) return <p className="text-sm text-muted-foreground">No alerts in the last 15 minutes.</p>;
  return alerts.map((a) => (
    <div key={a.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg" data-testid={`alert-${a.id}`}>
      <span className="text-xl">{a.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{a.title}</p>
        <p className="text-xs text-muted-foreground truncate">{a.message}</p>
      </div>
      <span className="text-[10px] text-muted-foreground">{timeAgo(a.created_at)}</span>
    </div>
  ));
}

function SessionInspector({ users, selectedUserId, onSelect, sessionDetail }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1">
        <CardHeader><CardTitle className="text-base">Sessions</CardTitle></CardHeader>
        <CardContent className="space-y-1 max-h-[60vh] overflow-y-auto">
          {users.map((u) => (
            <button key={u.session_id} type="button"
                    onClick={() => onSelect(u.user_id)}
                    className={`w-full text-left p-2 rounded-lg ${selectedUserId === u.user_id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/40"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${STATUS_COLOUR[u.status]}`} />
                <span className="text-sm font-medium truncate">{u.user_name}</span>
              </div>
              <p className="text-[11px] text-muted-foreground truncate">{u.current_feature || u.current_route}</p>
            </button>
          ))}
          {users.length === 0 && <p className="text-sm text-muted-foreground">No live sessions.</p>}
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">
            {sessionDetail?.presence?.user_name || (selectedUserId ? "Loading..." : "Select a user")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!selectedUserId && <p className="text-sm text-muted-foreground">Pick a session on the left.</p>}
          {sessionDetail?.presence && (
            <div className="grid grid-cols-2 gap-2 text-xs bg-muted/30 rounded-lg p-3">
              <Detail label="Route" value={sessionDetail.presence.current_route} />
              <Detail label="Feature" value={sessionDetail.presence.current_feature || "—"} />
              <Detail label="Modal" value={sessionDetail.presence.active_modal || "—"} />
              <Detail label="Last action" value={sessionDetail.presence.last_action || "—"} />
              <Detail label="Device" value={sessionDetail.presence.device || "?"} />
              <Detail label="Country" value={sessionDetail.presence.country || "—"} />
            </div>
          )}
          {sessionDetail?.events?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-muted-foreground">Last 25 events</p>
              <div className="space-y-1">
                {sessionDetail.events.map((e) => <EventRow key={e.id} e={e} />)}
              </div>
            </div>
          )}
          {sessionDetail?.errors?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-red-500">Recent errors</p>
              <div className="space-y-1">
                {sessionDetail.errors.map((er) => (
                  <div key={er.id} className="text-xs flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 mt-0.5 text-red-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{er.category} — {er.message}</p>
                      <p className="text-[10px] text-muted-foreground">{er.route} · {timeAgo(er.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium truncate">{value}</p>
    </div>
  );
}

/**
 * EnvBanner — pings /health/deploy on mount and renders a slim banner that
 * shows the live backend host, MongoDB host, db_name, fingerprint, and a
 * "drift" warning when production/preview signals look swapped. The point is
 * that an admin glancing at LiveOps can spot config drift in <2 seconds.
 */
function EnvBanner() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/health/deploy`, { timeout: 8000 });
        if (!cancelled) setData(r.data || null);
      } catch (e) {
        if (!cancelled) setErr(e.message || "unreachable");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Backend host as the user actually sees it
  const backendUrl = (() => {
    try { return new URL(API).host; } catch { return API; }
  })();
  const isProdHost = typeof window !== "undefined" &&
    window.location?.hostname === "duolyst.co.uk";

  if (err) {
    return (
      <div
        data-testid="admin-env-banner-error"
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-xs"
      >
        <WifiOff className="w-3.5 h-3.5" />
        <span className="font-mono truncate">backend {backendUrl} unreachable — {err}</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="admin-env-banner-loading"
        className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 text-muted-foreground text-xs"
      >
        <Activity className="w-3.5 h-3.5 animate-pulse" />
        <span className="font-mono truncate">checking deploy health…</span>
      </div>
    );
  }

  const fp = data.checks?.db_fingerprint || {};
  const dbHost = fp.host || "unknown";
  const dbName = fp.db_name || "unknown";
  const fingerprint = fp.fingerprint || "—";
  const userCount = fp.user_count ?? "—";
  const ok = !!data.ok;

  // Heuristic drift signals: prod frontend host but the DB host doesn't look
  // like the configured Atlas cluster, OR points at a known preview cluster.
  const driftSignals = [];
  if (isProdHost && /preview|emergent/i.test(dbHost)) {
    driftSignals.push("DB host looks like a preview cluster");
  }
  if (isProdHost && /preview|emergentagent/i.test(backendUrl)) {
    driftSignals.push("Backend URL looks like a preview host");
  }
  if (!ok) driftSignals.push("/health/deploy reports degraded subsystems");

  const drift = driftSignals.length > 0;

  return (
    <div
      data-testid="admin-env-banner"
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded-md border text-xs ${
        drift
          ? "border-amber-500/50 bg-amber-500/10 text-amber-200"
          : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
      }`}
    >
      <span className="flex items-center gap-1.5 font-medium">
        {drift ? <AlertTriangle className="w-3.5 h-3.5" /> : <Wifi className="w-3.5 h-3.5" />}
        {drift ? "Config drift suspected" : "Deploy looks healthy"}
      </span>
      <span className="font-mono opacity-80" data-testid="admin-env-backend">
        backend: {backendUrl}
      </span>
      <span className="font-mono opacity-80" data-testid="admin-env-db">
        db: {dbHost}/{dbName}
      </span>
      <span className="font-mono opacity-80" data-testid="admin-env-fingerprint">
        fp: {fingerprint}
      </span>
      <span className="font-mono opacity-60">
        users: {userCount}
      </span>
      {drift && (
        <span className="w-full font-mono opacity-90 mt-1" data-testid="admin-env-drift">
          ⚠ {driftSignals.join(" • ")}
        </span>
      )}
    </div>
  );
}

export default AdminLiveOpsTab;
